import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Hook for syncing state with URL search parameters
 * Supports string, number, and JSON-encoded values
 *
 * @param {Object} config - Configuration object with param definitions
 * @returns {Object} - Object with current values and setters
 *
 * @example
 * const { page, setPage, search, setSearch, filters, setFilters } = useURLState({
 *   page: { type: 'number', default: 1 },
 *   limit: { type: 'number', default: 10 },
 *   search: { type: 'string', default: '' },
 *   filters: { type: 'json', default: [] }
 * })
 */
const useURLState = (config) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const parseValue = useCallback((value, type, defaultValue) => {
    if (value === null || value === undefined) {
      return defaultValue
    }

    switch (type) {
      case 'number':
        const num = parseInt(value, 10)
        return isNaN(num) ? defaultValue : num
      case 'json':
        try {
          return JSON.parse(value)
        } catch {
          return defaultValue
        }
      case 'string':
      default:
        return value
    }
  }, [])

  const serializeValue = useCallback((value, type) => {
    if (value === null || value === undefined) {
      return null
    }

    switch (type) {
      case 'number':
        return String(value)
      case 'json':
        return JSON.stringify(value)
      case 'string':
      default:
        return value
    }
  }, [])

  const isDefaultValue = useCallback((value, defaultValue, type) => {
    if (type === 'json') {
      return JSON.stringify(value) === JSON.stringify(defaultValue)
    }
    return value === defaultValue
  }, [])

  const values = useMemo(() => {
    const result = {}
    Object.entries(config).forEach(([key, { type, default: defaultValue }]) => {
      result[key] = parseValue(searchParams.get(key), type, defaultValue)
    })
    return result
  }, [searchParams, config, parseValue])

  const createSetter = useCallback((key) => {
    return (newValue, options = {}) => {
      const { replace = false, resetPage = false } = options
      const { type, default: defaultValue } = config[key]

      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams)

        if (isDefaultValue(newValue, defaultValue, type)) {
          newParams.delete(key)
        } else {
          const serialized = serializeValue(newValue, type)
          if (serialized !== null) {
            newParams.set(key, serialized)
          }
        }

        // Reset page to 1 when other filters change (except page itself)
        if (resetPage && key !== 'page' && config.page) {
          newParams.delete('page')
        }

        return newParams
      }, { replace })
    }
  }, [config, setSearchParams, serializeValue, isDefaultValue])

  const setters = useMemo(() => {
    const result = {}
    Object.keys(config).forEach((key) => {
      const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1)
      result[`set${capitalizedKey}`] = createSetter(key)
    })
    return result
  }, [config, createSetter])

  // Batch update multiple params at once
  const setMultiple = useCallback((updates, options = {}) => {
    const { replace = false } = options

    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams)

      Object.entries(updates).forEach(([key, newValue]) => {
        if (!config[key]) return

        const { type, default: defaultValue } = config[key]

        if (isDefaultValue(newValue, defaultValue, type)) {
          newParams.delete(key)
        } else {
          const serialized = serializeValue(newValue, type)
          if (serialized !== null) {
            newParams.set(key, serialized)
          }
        }
      })

      return newParams
    }, { replace })
  }, [config, setSearchParams, serializeValue, isDefaultValue])

  // Clear all URL params
  const clearAll = useCallback((options = {}) => {
    const { replace = false } = options
    setSearchParams(new URLSearchParams(), { replace })
  }, [setSearchParams])

  return {
    ...values,
    ...setters,
    setMultiple,
    clearAll,
  }
}

export default useURLState
